// ============================================================
// Jede Route des Backends genau einmal, mit ihrem Rückgabetyp.
//
// Warum eine eigene Schicht über fetchJson: die Bildschirme sollen keine
// Pfade kennen. Wandert eine Route, ändert sich DIESE Datei — und der
// Übersetzer zeigt jeden Aufrufer, der nicht mitkommt. Die Reihenfolge folgt
// der Tabelle in SPEC §5.4.2; die Routen, die dort nicht stehen, aber im
// Namensvertrag (Anhang A.1), kommen am Ende und sind als solche markiert.
//
// Alle Pfade stehen OHNE das Präfix `/api`: das setzt fetchJson, und im Bau
// schneidet Traefiks stripprefix es vor dem Backend wieder ab (SPEC §2.8.1).
// FastAPI führt seine Routen an der Wurzel.
//
// ES GIBT KEINE ANDEREN ROUTEN (Anhang A.1). Insbesondere nicht:
// `POST /vorlagen`, `DELETE /vorlagen/{id}` (eine Vorlage ist ein Plan mit
// `ist_vorlage`, I2), kein `PATCH /plaene/{id}` (Umbenennen aus der Liste =
// `holePlan` + `speicherePlan`, §5.7.2) und `POST /admin/nutzer/{id}/rolle`
// heißt `PATCH`. Wer hier eine Route einträgt, die nicht in A.1 steht, baut
// einen 404 ein.
// ============================================================
import { deleteJson, getJson, patchJson, postForm, postJson, putJson } from "../lib/fetchJson";
import type { Plan } from "@kern";
import type {
  AuthConfig,
  Dateisatz,
  ExportArt,
  ExportAuftrag,
  FerienAntwort,
  FerienTabelle,
  Ich,
  ImportAuftrag,
  Nutzerzeile,
  PlanGespeichert,
  PlanKurz,
  PlanListe,
  Piktogramm,
  ClaudeAnfrage,
  ClaudeVerbindungen,
  PlanMeta,
  PlanVoll,
  Rolle,
  Seitenformat,
  StandKurz,
  StandVoll,
  StartAntwort,
  Uebernahme,
  VerifyAntwort,
  Vorlage,
} from "./typen";

/**
 * Suchparameter bauen und dabei leere Werte weglassen. Eine Liste wird zu
 * MEHREREN Parametern desselben Namens (`schlagwort=a&schlagwort=b`) — das
 * Backend verknüpft sie mit UND (§5.4.2).
 */
function frage(params: Record<string, string | number | boolean | string[] | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === "") continue;
    if (Array.isArray(v)) {
      for (const e of v) if (e !== "") p.append(k, e);
      continue;
    }
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

/* --- Anmeldung ----------------------------------------------------------- */

export const holeAuthConfig = () => getJson<AuthConfig>("/auth/config");

export const codeAnfordern = (email: string, turnstile: string | null) =>
  postJson<StartAntwort>("/auth/start", { email, turnstile });

export const codeEinloesen = (challenge: string, code: string, angemeldet_bleiben: boolean) =>
  postJson<VerifyAntwort>("/auth/verify", { challenge, code, angemeldet_bleiben });

export const abmelden = () => postJson<{ ok: boolean }>("/auth/logout");

/* --- Ich ----------------------------------------------------------------- */

export const holeMich = () => getJson<Ich>("/me");

export const setzeMich = (patch: { anzeigename: string }) => patchJson<Ich>("/me", patch);

/* --- Pläne --------------------------------------------------------------- */

export type PlanSortierung = "geaendert" | "titel" | "kunde" | "angelegt" | "zeitraum" | "eintraege";
export type Sortierrichtung = "auf" | "ab";

/** Die Parameter von `GET /plaene`. Alles freiwillig; `0`/`1` für die Schalter. */
export interface PlanFilter {
  q?: string;
  kunde?: string;
  /** mehrfach möglich; das Backend verknüpft sie mit UND */
  schlagwort?: string[];
  nur_meine?: 0 | 1;
  vorlagen?: 0 | 1;
  papierkorb?: 0 | 1;
  sort?: PlanSortierung;
  richtung?: Sortierrichtung;
  limit?: number;
  offset?: number;
}

export const holePlaene = (f: PlanFilter = {}) => getJson<PlanListe>("/plaene" + frage({ ...f }));

export const holePlan = (id: string) => getJson<PlanVoll>(`/plaene/${id}`);

/** Vorlagen-Kurzname (`lilasp`|`isek`|`leer`) ODER `vorlage_id` ODER `daten`. */
export interface PlanAnlegen {
  titel?: string;
  kunde?: string;
  projekt_nr?: string;
  schlagworte?: string[];
  vorlage?: "lilasp" | "isek" | "leer";
  vorlage_id?: string;
  daten?: Plan;
}

export const legePlanAn = (b: PlanAnlegen) => postJson<PlanVoll>("/plaene", b);

/**
 * Der Rumpf trägt die `version`, die der Client zuletzt bestätigt bekam
 * (optimistische Sperre, E4/T6). Bei **409** wirft `fetchJson` einen
 * `ApiError`, dessen `body` den Konfliktrumpf trägt
 * (`{detail, aktuelle_version, geaendert_von, geaendert_am}`).
 * **Die Version wird NIE aus einem 409 übernommen** — sonst ist die Sperre
 * ausgehebelt (§5.2.2 Regel 4).
 */
export interface PlanSpeichern {
  version: number;
  daten: Plan;
  /** true = ausdrücklich einen Stand festhalten */
  stand?: boolean;
  notiz?: string;
  titel?: string;
  kunde?: string;
  projekt_nr?: string;
  schlagworte?: string[];
}

export const speicherePlan = (id: string, b: PlanSpeichern) => putJson<PlanGespeichert>(`/plaene/${id}`, b);

export const loeschePlan = (id: string) => deleteJson<{ geloescht_am: string }>(`/plaene/${id}`);

export const stellePlanWiederHer = (id: string) => postJson<PlanKurz>(`/plaene/${id}/wiederherstellen`);

export const kopierePlan = (id: string, titel?: string) => postJson<PlanVoll>(`/plaene/${id}/kopie`, titel ? { titel } : {});

export const alsVorlage = (id: string, titel: string) => postJson<PlanKurz>(`/plaene/${id}/als-vorlage`, { titel });

/* --- Stände -------------------------------------------------------------- */

export const holeStaende = (id: string) => getJson<{ staende: StandKurz[] }>(`/plaene/${id}/staende`);

export const halteStandFest = (id: string, version: number, notiz: string) =>
  postJson<StandKurz>(`/plaene/${id}/staende`, { version, notiz });

export const holeStand = (plan_id: string, version: number) => getJson<StandVoll>(`/staende/${plan_id}/${version}`);

/**
 * Zurückspringen. Der Rumpf trägt die **aktuelle** Planfassung (nicht die des
 * Stands) — die Route prüft sie und antwortet sonst mit demselben 409 wie das
 * `PUT` (Anhang A.3).
 */
export const setzeAufStandZurueck = (plan_id: string, stand_version: number, aktuelle_version: number) =>
  postJson<PlanKurz>(`/staende/${plan_id}/${stand_version}/wiederherstellen`, { version: aktuelle_version });

/* --- Vorlagen ------------------------------------------------------------ */

export const holeVorlagen = () => getJson<{ vorlagen: Vorlage[] }>("/vorlagen");

/* --- Ferien -------------------------------------------------------------- */

/** `bl` ist Pflicht (Bundesland-Kürzel, z. B. "HH"). */
export const holeFerien = (bl: string) => getJson<FerienAntwort>("/ferien" + frage({ bl }));

/* --- Export -------------------------------------------------------------- */

export interface ExportAnstoss {
  art: ExportArt;
  format?: Seitenformat;
  kopfzeile?: boolean;
  hintergrund?: boolean;
  dpi?: number;
}

export const starteExport = (plan_id: string, b: ExportAnstoss) => postJson<ExportAuftrag>(`/plaene/${plan_id}/exporte`, b);

export const holeExport = (id: string) => getJson<ExportAuftrag>(`/exporte/${id}`);

/**
 * Adresse einer Datei — KEIN `fetchJson`, sondern direkt an `<a href>` oder
 * `<img src>`. Der Strom läuft durch das Backend und prüft die Sitzung; es
 * gibt keine Presigned-URLs nach außen (T7).
 * `/api/dateien/{id}` **ohne** `/datei` liefert nur die Metadaten.
 */
export const dateiAdresse = (datei_id: string) => `/api/dateien/${datei_id}/datei`;

/** Dasselbe für das Erzeugnis eines Export-Auftrags. */
export const exportAdresse = (export_id: string) => `/api/exporte/${export_id}/datei`;

/* --- Import -------------------------------------------------------------- */

/**
 * Mehrteilig, Feld `datei` (dazu freiwillig `plan_id`). KEIN Content-Type
 * setzen — die Trennmarke kennt nur der Browser (`postForm`).
 */
export const starteImport = (form: FormData) => postForm<ImportAuftrag>("/importe", form);

export const holeImport = (id: string) => getJson<ImportAuftrag>(`/importe/${id}`);

/** Legt IMMER einen neuen Plan an (T40/A11) — nie ein Abgleich mit einem bestehenden. */
export const uebernehmeImport = (id: string, b: Uebernahme) => postJson<PlanVoll>(`/importe/${id}/uebernehmen`, b);

export const verwerfeImport = (id: string) => deleteJson<void>(`/importe/${id}`);

/* --- Verwaltung ---------------------------------------------------------- */

export const holeNutzer = () => getJson<{ nutzer: Nutzerzeile[] }>("/admin/nutzer");

/** PATCH, nicht POST (Anhang A.1). */
export const setzeRolle = (id: string, rolle: Rolle) => patchJson<Nutzerzeile>(`/admin/nutzer/${id}/rolle`, { rolle });

export const entsperre = (email: string) => postJson<{ entsperrt: number }>("/admin/entsperren", { email });

/* ==========================================================================
   Routen aus Anhang A.1, die in der Tabelle §5.4.2 nicht aufgeführt sind.
   Sie sind Teil desselben Vertrags; hier stehen sie, damit niemand sie
   „nebenbei" mit einem rohen fetch aufruft.
   ========================================================================== */

/**
 * Datei hochladen (mehrteilig, Feld `datei`). Eigene Wurzel `/dateien`, NICHT
 * unter `/plaene/…`: die Rumpfgrenze der Middleware entscheidet am
 * Pfad-Präfix, und läge der Upload unter `/plaene/`, bekäme auch
 * `PUT /plaene/{id}` den 26-MB-Deckel (A8).
 */
export function ladeDateiHoch(datei: File, plan_id?: string, art: "quelle" | "logo" = "quelle") {
  const fd = new FormData();
  fd.append("datei", datei);
  if (plan_id) fd.append("plan_id", plan_id);
  fd.append("art", art);
  return postForm<Dateisatz>("/dateien", fd);
}

export const holeDatei = (id: string) => getJson<Dateisatz>(`/dateien/${id}`);

// ----------------------------------------------------- Bürobibliothek (T44)

export const holePiktogramme = () => getJson<{ piktogramme: Piktogramm[] }>("/piktogramme");

/** SVG hochladen. Der Server liest daraus nur die Geometrie; die Datei selbst behält er nicht. */
export function ladePiktogrammHoch(datei: File, name?: string) {
  const fd = new FormData();
  fd.append("datei", datei);
  if (name) fd.append("name", name);
  return postForm<Piktogramm>("/piktogramme", fd);
}

export const loeschePiktogramm = (id: string) => deleteJson<void>(`/piktogramme/${id}`);

export const holePlanDateien = (plan_id: string) => getJson<{ dateien: Dateisatz[] }>(`/plaene/${plan_id}/dateien`);

export const loescheDatei = (id: string) => deleteJson<void>(`/dateien/${id}`);

/** Die letzten 20 eigenen Import-Aufträge, ohne `ergebnis`. */
export const holeImporte = () => getJson<{ importe: ImportAuftrag[] }>("/importe");

/** Die letzten 20 Export-Aufträge eines Plans. */
export const holePlanExporte = (plan_id: string) => getJson<{ exporte: ExportAuftrag[] }>(`/plaene/${plan_id}/exporte`);

/**
 * Die Ferientabelle in GENAU dem Format des Prototyps (base36, A4) — der Kern
 * faltet sie mit `ferienSegs`. Für den Editor ist `holeFerien(bl)` der
 * bequemere Weg; diese Route liefert alle Länder auf einmal.
 *
 * `daten` und `letztes_segment` sind je ein SATZ über die 16 Bundesländer
 * (`daten["HE"]` ist die base36-Zeichenkette, `letztes_segment["HE"]` ein
 * ISO-Datum), nicht je ein einzelner Wert — so zeigt es §4.5.10, so liefert es
 * `routers/ferien.py`, und so erwartet es `window.ferienSetzen({epoche, namen,
 * daten})` (A.7). Mit `daten: string` käme `daten[plan.bl]` als ein ZEICHEN
 * heraus, und niemand sähe den Fehler vor dem ersten leeren Ferienband.
 */
export const holeFerienTabelle = () =>
  getJson<FerienTabelle>("/ferien/tabelle");

/** Endgültig löschen — nur Admin, in der Oberfläche absichtlich ohne Knopf (B11/I9). */
export const loeschePlanEndgueltig = (id: string) => deleteJson<void>(`/plaene/${id}/endgueltig`);

/** Ferien neu holen — nur Admin (T38, jährlich im Juli). */
export const ferienAktualisieren = () =>
  postJson<{ geholt: number; neu: number; entfernt: number; laender: number; quelle: string }>("/admin/ferien/aktualisieren");

// --- T48: Claude-Zugang -----------------------------------------------------
// Die OAuth-Wege, die die OBERFLÄCHE braucht (Freigabe, Verbindungen). Token,
// Registrierung und /mcp ruft nur Claude selbst — nie diese Anwendung.

export const holeClaudeAnfrage = (id: string) =>
  getJson<ClaudeAnfrage>(`/oauth/anfragen/${encodeURIComponent(id)}`);

export const entscheideClaudeAnfrage = (id: string, erlauben: boolean) =>
  postJson<{ weiter: string }>(`/oauth/anfragen/${encodeURIComponent(id)}`, { erlauben });

export const holeClaudeVerbindungen = () => getJson<ClaudeVerbindungen>("/oauth/verbindungen");

export const trenneClaudeVerbindung = (id: string) =>
  deleteJson<void>(`/oauth/verbindungen/${encodeURIComponent(id)}`);

export const uebernehmeEntwurf = (planId: string) =>
  postJson<PlanMeta>(`/plaene/${encodeURIComponent(planId)}/entwurf-uebernehmen`);
